/**
 * ============================================================================
 * LabelManagementPage — 다국어 라벨 관리 (V22 vertical 정규화)
 * ============================================================================
 *
 * 【역할】
 *   UI 버튼/폼 라벨/플레이스홀더(fw_label) 마스터 CRUD.
 *   MessageManagement 가 알림/경고 메시지를, 본 화면이 라벨/플레이스홀더를 담당.
 *   V22 부터 locale 별 row 수직(vertical) 정규화 — 한 labelKey × N locale.
 *
 * 【주요 기능】
 *   - 검색: keyword + category 필터
 *   - CRUD: 인라인 편집(labelId/locale/value/useYn) + 벌크 저장
 *   - 기본값(defaultValue) 회색 표시로 번역 가이드 제공
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=label/searchListVertical) — V22 vertical 조회
 *   - POST /api/v1/transaction (service=label/saveVertical)       — V23 vertical 저장
 *
 * 【DataSet/Hook】
 *   - useDataSet<LabelRow>() — ds_labelList : 라벨 목록 (PK: labelKey+locale)
 *   - useTransaction()       — RPC 통합 호출
 *   - useLabel()             — i18n 라벨(t)
 *   - useSupportedLocales()  — locale 드롭다운 옵션
 *
 * 【사용처】 router/index.tsx — `{ path: '/label', element: <LabelManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageLabelManagement.vue`
 * 【의존성】 antd Form/Input/Select/Tag, framework hooks (makeColumns)
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, Select, Tag } from 'antd'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { useSupportedLocales } from '@/framework/useLocale'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 라벨 1행 — V22 vertical(locale 별) + 구 평면화(textKo/En/...) 호환. */
interface LabelRow {
  /** 라벨 ID PK 일부 (예: "BTN_SAVE", "LBL_USER_NAME"). */
  labelId: string
  /** 라벨 키 (서버 별칭, labelId 와 동일 사용 가능). */
  labelKey?: string
  /** 카테고리 (예: "label", "button", "placeholder"). 분류용. */
  category?: string
  /** 로케일 코드 (예: "ko"·"en"). PK 일부. */
  locale?: string
  /** 로케일 표시명 (서버 join). */
  localeName?: string
  /** 국기 이모지 (서버 join). */
  flagEmoji?: string
  /** 해당 locale 의 라벨 텍스트 (실제 편집 대상). */
  value?: string
  /** 기본 locale 의 값 (회색 텍스트, 편집 불가). */
  defaultValue?: string
  /** 라벨 설명/사용처 (운영자용). */
  description?: string
  /** 사용여부 ('Y'=노출 / 'N'=숨김). */
  useYn: 'Y' | 'N'
  // (구) 4언어 평면화 호환 — 사용 안 함
  /** [legacy] 한국어. */
  textKo?: string
  /** [legacy] 영어. */
  textEn?: string
  /** [legacy] 중국어. */
  textZh?: string
  /** [legacy] 일본어. */
  textJa?: string
  /** [legacy] 베트남어. */
  textVi?: string
}

/**
 * LabelManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function LabelManagementPage() {
  const ds = useDataSet<LabelRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const supportedLocales = useSupportedLocales()
  const localeOptions = useMemo(
    () =>
      supportedLocales.map((l) => ({
        value: l.locale,
        label: `${l.flagEmoji ?? ''} ${l.locale.toUpperCase()} · ${l.nativeName ?? l.name ?? l.locale}`
      })),
    [supportedLocales]
  )
  const [searchForm] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)

  /**
   * 라벨 검색 — label/searchListVertical → ds.
   * @param values 외부 폼 값 (생략 시 내부 searchForm)
   * loading: false 옵션으로 전역 로딩 대신 자체 isLoading state 사용.
   */
  const search = async (values?: Record<string, unknown>) => {
    setIsLoading(true)
    try {
      await transaction({
        id: 'select',
        // V22: locale 별 row 정규화(수직) 형태 — 한 라벨 × 5 locale
        service: 'label/searchListVertical',
        inds: { ds_search: values ?? searchForm.getFieldsValue() },
        outds: { ds_labelList: ds },
        loading: false
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    search()
  }, [])

  /** 신규 라벨 행 추가 — useYn 기본값 'Y'. */
  const add = () =>
    ds.addRow({
      labelId: '',
      useYn: 'Y',
      textKo: '',
      textEn: '',
      textZh: '',
      textJa: '',
      textVi: ''
    } as LabelRow)
  /** 선택행 삭제 — DataSet C/U/D 추적. */
  const del = () => ds.selectedIndex >= 0 && ds.deleteRow(ds.selectedIndex)
  /** 벌크 저장 — label/saveVertical (vertical 단위 C/U/D). 성공 시 재조회. */
  const save = async () => {
    setIsLoading(true)
    try {
      const res = await transaction({
        id: 'save',
        // V23: vertical 형태 (labelKey + locale + value) 단위 저장
        service: 'label/saveVertical',
        action: 'save',
        inds: { ds_labelList: ds },
        outds: { ds_labelList: ds },
        loading: false
      })
      if (res.success) await search()
    } finally {
      setIsLoading(false)
    }
  }

  /** 셀 단위 단일 필드 갱신 헬퍼 — DataSet 자동 _state='U' 마킹. */
  const patch = (idx: number, key: keyof LabelRow, v: unknown) =>
    ds.updateRow(idx, { [key]: v } as Partial<LabelRow>)

  // V22: locale 별 row 정규화 — label_key × locale 매트릭스를 수직(vertical)으로 표시
  // 컬럼: 라벨ID / 카테고리 / 로케일 / 값 / 기본값 / 사용 / 상태
  const columns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<LabelRow>>[] = [
      {
        key: 'COL_LABEL_ID',
        dataIndex: ['data', 'labelId'],
        width: 220,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.labelId}
            disabled={r._state !== 'C'}
            onChange={(e) => patch(i, 'labelId', e.target.value)}
          />
        )
      },
      {
        key: 'COL_CATEGORY',
        dataIndex: ['data', 'category'],
        width: 110,
        render: (_v, r) => <Tag>{r.data.category || 'label'}</Tag>
      },
      {
        key: 'COL_LOCALE',
        fallback: '로케일',
        dataIndex: ['data', 'locale'],
        width: 200,
        render: (_v, r, i) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={r.data.locale}
            options={localeOptions}
            onChange={(v) => patch(i, 'locale', v)}
          />
        )
      },
      {
        key: 'COL_VALUE',
        fallback: '값',
        dataIndex: ['data', 'value'],
        width: 320,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.value}
            onChange={(e) => patch(i, 'value', e.target.value)}
          />
        )
      },
      {
        key: 'COL_DEFAULT_VALUE',
        fallback: '기본값',
        dataIndex: ['data', 'defaultValue'],
        width: 200,
        render: (_v, r) => <span style={{ color: '#999' }}>{r.data.defaultValue}</span>
      },
      {
        key: 'COL_USE_YN',
        dataIndex: ['data', 'useYn'],
        width: 90,
        render: (_v, r, i) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={r.data.useYn}
            options={[
              { label: t('LBL_YES', '사용'), value: 'Y' },
              { label: t('LBL_NO', '미사용'), value: 'N' }
            ]}
            onChange={(v) => patch(i, 'useYn', v)}
          />
        )
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t, ds, localeOptions])

  return (
    <AppPage
      search={{
        form: searchForm,
        onSearch: () => search(),
        content: (
          <>
            <Form.Item label={t('LBL_KEYWORD', '검색어')} name="keyword">
              <FormInput />
            </Form.Item>
            <Form.Item label={t('LBL_CATEGORY', '카테고리')} name="category">
              <FormInput />
            </Form.Item>
          </>
        )
      }}
      toolbar={{
        onSearch: () => search(),
        onAdd: add,
        onDelete: del,
        onSave: save,
        dirty: ds.isDirty
      }}
    >
      <DataTable<TrackedRow<LabelRow>>
        rowKey="_rowId"
        dataSource={ds.getVisibleRows()}
        columns={columns}
        loading={isLoading}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: ds.selectedRow ? [ds.selectedRow._rowId] : [],
          onChange: (keys) => {
            const idx = ds.rows.findIndex((r) => r._rowId === keys[0])
            ds.setSelectedIndex(idx)
          }
        }}
      />
    </AppPage>
  )
}
